import { Fragment } from "react";
import { motion } from "framer-motion";
import { Copy, ChevronLeft, ChevronRight, CheckCircle2 } from "lucide-react";
import { MockShell, Spot } from "../Frames";

const WEEKS = [["11 Jun", "12 Jun", "13 Jun", "14 Jun", "15 Jun", "16 Jun", "17 Jun"], ["18 Jun", "19 Jun", "20 Jun", "21 Jun", "22 Jun", "23 Jun", "24 Jun"]];
const CREW = [
  { name: "Jack Murray", casual: false, shifts: ["Banksia Cres", "Banksia Cres", "", "", "Wattle St", "Wattle St", "Ryrie St"] },
  { name: "Liam O'Brien", casual: false, shifts: ["Wattle St", "Wattle St", "", "", "Banksia Cres", "Ryrie St", "Ryrie St"] },
  { name: "Mia Russo", casual: true, shifts: ["Ryrie St", "", "", "", "", "Federation Dr", ""] },
  { name: "Sam Nguyen", casual: true, shifts: ["", "Federation Dr", "", "", "Federation Dr", "", ""] },
];
const ANSWERS = { "Mia Russo-0": "Accepted", "Mia Russo-4": "Pending", "Mia Russo-5": "Accepted", "Sam Nguyen-1": "Accepted", "Sam Nguyen-4": "Declined" };
const PILL = { Accepted: "bg-emerald-100 text-emerald-700", Pending: "bg-amber-100 text-amber-700", Declined: "bg-red-100 text-red-700" };

const Cell = ({ site, status, on, fresh, pop }) => {
  if (!site) return <div className="h-11 rounded-md border border-dashed border-slate-200" />;
  return (
    <Spot on={on}>
      <motion.div initial={fresh ? { opacity: 0, scale: 0.8 } : false} animate={{ opacity: 1, scale: 1 }} transition={{ delay: fresh ? 0.9 : 0 }}
        className="h-11 rounded-md bg-[#E6EEF8] border border-[#C5D5EA] px-1.5 py-1">
        <p className="text-[10px] font-semibold truncate text-[#1E3A5F]">{site}</p>
        {status && <span className={`mt-0.5 inline-block rounded-full px-1.5 text-[8.5px] font-bold ${PILL[status]}`}>{status}</span>}
      </motion.div>
      {pop && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
          className="absolute left-0 top-14 w-52 z-20 rounded-lg bg-white border border-slate-200 shadow-xl p-3 space-y-2">
          <p className="font-display font-bold text-[12px]">Add shift · Mia, 15 Jun</p>
          <div className="h-7 rounded-md border border-slate-200 px-2 flex items-center text-[11px]">Lot 88 Federation Dr</div>
          <div className="h-7 rounded-full bg-[#1E3A5F] text-white grid place-items-center text-[11px] font-semibold">Save &amp; notify</div>
        </motion.div>
      )}
    </Spot>
  );
};

export const RosterScreens = ({ step }) => {
  const copied = step === 3;
  return (
    <MockShell tab="Roster">
      <div className="flex items-center gap-3 mb-3">
        <p className="font-display font-bold text-[16px]">Roster</p>
        <Spot on={step === 0}>
          <span className="inline-flex items-center gap-1 rounded-full bg-white border border-slate-200 px-2 py-1 font-mono text-[11px] font-semibold">
            <ChevronLeft className="w-3 h-3" />{copied ? "18 – 24 Jun" : "11 – 17 Jun"}<ChevronRight className="w-3 h-3" />
          </span>
        </Spot>
        <div className="ml-auto">
          <Spot on={copied}>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-3 py-1.5 text-[11px] font-semibold"><Copy className="w-3 h-3" /> Copy last week</span>
          </Spot>
        </div>
      </div>
      <div className="grid grid-cols-[104px_repeat(7,1fr)] gap-1.5">
        <div />
        {WEEKS[copied ? 1 : 0].map((d) => <p key={d} className="text-[10px] font-mono font-semibold text-slate-500 text-center pb-1">{d}</p>)}
        {CREW.map((c) => (
          <Fragment key={c.name}>
            <div className="flex flex-col justify-center">
              <p className="font-semibold text-[11px]">{c.name}</p>
              <p className="text-[9px] text-slate-500">{c.casual ? "Casual" : "Full-time"}</p>
            </div>
            {c.shifts.map((s, d) => {
              const key = `${c.name}-${d}`;
              const added = key === "Mia Russo-4";
              const site = added && step >= 1 ? "Federation Dr" : s;
              const status = c.casual && site ? (copied ? "Pending" : step === 2 ? ANSWERS[key] : null) : null;
              return (
                <Cell key={key} site={site} status={status} fresh={(added && step === 1) || (copied && !!site)}
                  on={(added && step === 1) || (step === 2 && d === 4 && !!status)} pop={added && step === 1} />
              );
            })}
          </Fragment>
        ))}
      </div>
      {copied && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3 }}
          className="absolute right-4 bottom-4 flex items-center gap-2 rounded-lg bg-[#0F172A] text-white px-3 py-2 text-[11px] font-semibold shadow-xl">
          <CheckCircle2 className="w-4 h-4 text-emerald-400" /> Copied 15 shifts from last week
        </motion.div>
      )}
    </MockShell>
  );
};
